import React, { useState, useCallback, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Container,
  Stack,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { RouterLink } from 'src/routes/components';
import Iconify from 'src/components/iconify';
import { getCandidateDetailsByJobId } from 'src/services/candidate.service';
import { useNavigate, useParams } from 'react-router';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { LoadingScreen } from 'src/components/loading-screen';
import { useSettingsContext } from 'src/components/settings';
import { getJobDetails } from 'src/services/role.service';
import {
  InterviewDetails,
  PreScreening,
  Profile,
  ProfileMatch,
} from 'src/sections/candidates/tabs';
import { useQuery } from 'src/hooks/use-query';
import { paths } from 'src/routes/paths';
import { useCreateSearchParams } from 'src/hooks/use-create-search-params';
import PropTypes from 'prop-types';

const CANDIDATE_DETAILS_TABS = [
  { value: 'profile', label: 'Profile' },
  { value: 'profileMatch', label: 'Profile Match' },
  { value: 'preScreening', label: 'Pre-screening' },
  { value: 'interviewDetails', label: 'Interview Details' },
];

const JobCandidateDetailsView = ({ role_id }) => {
  // states
  const [currentTab, setCurrentTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [candidateChanged, setCandidateChanged] = useState(false);
  const [jobCandidateError, setJobCandidateError] = useState(null);

  // hooks
  const settings = useSettingsContext();
  const params = useParams();
  const { jobId } = params;
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const organization_id = getOrganizationId(user);

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const renderTabs = (
    <Tabs
      value={currentTab}
      onChange={handleChangeTab}
      sx={{
        mb: { xs: 3, md: 5 },
      }}
    >
      {CANDIDATE_DETAILS_TABS.map(tab => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
        />
      ))}
    </Tabs>
  );

  useEffect(() => {
    const caller = async () => {
      try {
        const candidateData = await getCandidateDetailsByJobId(
          organization_id,
          jobId,
          params.candidateId,
        );
        if (candidateData.success) {
          setCandidateDetails(candidateData.body);
          const jobData = await getJobDetails(organization_id, jobId);
          if (jobData.success) {
            setJobDetails(jobData.body);
          }
        } else {
          setJobCandidateError('Error getting candidate details');
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    caller();
  }, [candidateChanged, jobId, organization_id, params.candidateId]);

  const searchQuery = useQuery();
  const tab = searchQuery.get('tab');
  const tablePage = searchQuery.get('tablePage');
  const searchParams = useCreateSearchParams({
    tab,
    tablePage,
  });

  const handleBackButton = useCallback(() => {
    if (tab || tablePage) {
      navigate(`${paths.dashboard.jobs.details(jobId)}?${searchParams}`);
    } else {
      navigate(-1);
    }
  }, [jobId, navigate, searchParams, tab, tablePage]);

  return (
    <>
      {loading && <LoadingScreen loadingText="Loading..." />}
      {jobCandidateError && (
        <Container maxWidth={settings.themeStretch ? false : 'lg'}>
          <Stack
            spacing={1.5}
            direction="row"
            alignItems="center"
            sx={{
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box>
              <Button
                onClick={handleBackButton}
                startIcon={
                  <Iconify icon="eva:arrow-ios-back-fill" width={16} />
                }
              >
                Back
              </Button>
            </Box>
          </Stack>
          <Typography variant="h4">{jobCandidateError}</Typography>
        </Container>
      )}
      {!loading && candidateDetails && (
        <Container maxWidth={settings.themeStretch ? false : 'lg'}>
          <Stack
            spacing={1.5}
            direction="row"
            alignItems="center"
            sx={{
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ mb: '1rem' }}>
                <Typography variant="h4">
                  {candidateDetails?.candidate_info?.name}
                </Typography>
              </Box>
              <Box>
                <Button
                  onClick={handleBackButton}
                  startIcon={
                    <Iconify icon="eva:arrow-ios-back-fill" width={16} />
                  }
                >
                  Back
                </Button>
              </Box>
            </Box>
          </Stack>

          {renderTabs}

          {currentTab === 'profile' && (
            <Profile
              setCandidateChanged={setCandidateChanged}
              candidateDetails={candidateDetails}
            />
          )}
          {currentTab === 'profileMatch' && (
            <ProfileMatch
              setCandidateChanged={setCandidateChanged}
              candidateDetails={candidateDetails}
              jobDetails={jobDetails}
            />
          )}
          {currentTab === 'preScreening' && (
            <PreScreening jobId={jobId} candidateDetails={candidateDetails} />
          )}
          {currentTab === 'interviewDetails' && (
            <InterviewDetails
              jobId={jobId}
              candidateDetails={candidateDetails}
            />
          )}
        </Container>
      )}
    </>
  );
};

JobCandidateDetailsView.propTypes = {
  role_id: PropTypes.string,
};

export default JobCandidateDetailsView;
